import React, { useEffect, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import DownloadOutlined from '@mui/icons-material/Download'
import RotateLeft from '@mui/icons-material/RotateLeft'
import RotateRight from '@mui/icons-material/RotateRight'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import Undo from '@mui/icons-material/Undo'
import ZoomIn from '@mui/icons-material/ZoomIn'
import ZoomOut from '@mui/icons-material/ZoomOut'
import { Box, IconButton, Modal } from '@mui/material'

interface ImagePreviewProps {
  imageString: string
  setImage: React.Dispatch<React.SetStateAction<string>>
}

const isVideoFile = (url: string) => {
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov']
  const extension = url.split('.').pop()?.toLowerCase()

  
return extension ? videoExtensions.includes(extension) : false
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ imageString, setImage }) => {
  const [open, setOpen] = useState(false)
  const [scale, setScale] = useState(100)
  const [rotate, setRotate] = useState(0)
  const [flipped, setFlipped] = useState({ x: false, y: false })

  const handleOpen = () => setOpen(true)

  const handleClose = () => {
    setOpen(false)
    setImage('')
  }

  const onDownload = () => {
    const url = imageString
    const extension = url.split('.').pop()?.toLowerCase() || 'png'
    const filename = `media-${Date.now()}.${extension}`

    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        URL.revokeObjectURL(blobUrl)
        link.remove()
      })
  }

  const handleZoomIn = () => setScale(prev => Math.min(prev + 10, 200))
  const handleZoomOut = () => setScale(prev => Math.max(prev - 10, 50))
  const handleRotateLeft = () => setRotate(prev => prev - 90)
  const handleRotateRight = () => setRotate(prev => prev + 90)
  const handleFlipX = () => setFlipped(prev => ({ ...prev, x: !prev.x }))
  const handleFlipY = () => setFlipped(prev => ({ ...prev, y: !prev.y }))

  const handleReset = () => {
    setScale(100)
    setRotate(0)
    setFlipped({ x: false, y: false })
  }

  useEffect(() => {
    if (imageString === '') return
    setScale(100)
    setRotate(0)
    setFlipped({ x: false, y: false })
    handleOpen()
  }, [imageString])

  return (
    <>
      <Modal open={open} onClose={handleClose} sx={{ maxWidth: 'xl', margin: 'auto' }}>
        <>
          <Box display='flex' justifyContent='center' alignItems='center' sx={{ height: '100%' }}>
            <Box
              sx={{
                transform: `scale(${scale / 100}) rotate(${rotate}deg)`,
                transition: 'transform 0.3s'
              }}
            >
              {isVideoFile(imageString) ? (
                <video
                  src={imageString}
                  controls
                  muted
                  loop
                  autoPlay
                  playsInline
                  style={{
                    maxHeight: '80vh',
                    maxWidth: '80vw',
                    transform: flipped.x ? 'scaleY(-1)' : flipped.y ? 'scaleX(-1)' : ''
                  }}
                />
              ) : (
                <img
                  src={imageString}
                  alt={`Image ${imageString}`}
                  style={{
                    maxHeight: '80vh',
                    maxWidth: '80vw',
                    transform: flipped.x ? 'scaleY(-1)' : flipped.y ? 'scaleX(-1)' : ''
                  }}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
            <IconButton onClick={handleClose} sx={{ color: 'white', backgroundColor: 'white' }}>
              <CloseIcon sx={{ color: 'primary.main' }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 5,
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: 1,
              borderRadius: 1
            }}
          >
            {/* <IconButton onClick={onDownload} sx={{ color: 'white', fontSize: '1.5rem' }}>
              <DownloadOutlined />
            </IconButton> */}
            <IconButton onClick={handleFlipY} sx={{ color: 'white', fontSize: '1.5rem' }}>
              <SwapHorizIcon />
            </IconButton>
            <IconButton onClick={handleFlipX} sx={{ color: 'white', fontSize: '1.5rem' }}>
              <SwapVertIcon />
            </IconButton>
            <IconButton onClick={handleRotateLeft} sx={{ color: 'white', fontSize: '1.5rem' }}>
              <RotateLeft />
            </IconButton>
            <IconButton onClick={handleRotateRight} sx={{ color: 'white', fontSize: '1.5rem' }}>
              <RotateRight />
            </IconButton>
            <IconButton onClick={handleZoomOut} sx={{ color: 'white', fontSize: '1.5rem' }}>
              <ZoomOut />
            </IconButton>
            <IconButton onClick={handleZoomIn} sx={{ color: 'white', fontSize: '1.5rem' }}>
              <ZoomIn />
            </IconButton>
            <IconButton onClick={handleReset} sx={{ color: 'white', fontSize: '1.5rem' }}>
              <Undo />
            </IconButton>
          </Box>
        </>
      </Modal>
    </>
  )
}

export default ImagePreview
